import React, { useEffect, useRef, useState } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  ChangeCircle as ChangeIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  DataObject as ExtractIcon,
} from '@mui/icons-material';
import { storage } from '../../../../../../config/firebase';
import { ref, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';
import { formatPhoneNumber } from '../utils/formatters';
import { COUNTRY_CODES } from '../utils/constants';

// Import PDF viewer components
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { thumbnailPlugin } from '@react-pdf-viewer/thumbnail';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import * as pdfjsLib from 'pdfjs-dist';
import { formatCAR } from '../utils/formatters';
import { createTimestamp } from '../../../../../../utils/date-utils';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/thumbnail/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.0.279/pdf.worker.min.js`;

const isMD5 = (str) => {
  return /^[a-f0-9]{32}$/i.test(str);
};

// Function to convert PDF to JPG
const convertPDFtoJPG = async (pdfFile) => {
  try {
    // Read the PDF file
    const arrayBuffer = await pdfFile.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    
    // Get the first page
    const page = await pdf.getPage(1);
    
    // Set desired dimensions
    const viewport = page.getViewport({ scale: 2.0 }); // Increase scale for better quality
    
    // Prepare canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    
    // Render PDF page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;
    
    // Convert canvas to JPG blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95); // 95% quality
    });
  } catch (error) {
    console.error('Error converting PDF to JPG:', error);
    throw error;
  }
};

// Add this function to handle PDF fetching with CORS
const fetchPdfWithCors = async (url) => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept': 'application/pdf',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching PDF:', error);
    toast.error('Error loading PDF file. Please try again.');
    throw error;
  }
};

const FileUploadSection = ({ certification, onFileSelect, handleFieldChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedJpg, setSelectedJpg] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [viewingFileType, setViewingFileType] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isValidKey, setIsValidKey] = useState(false);
  const [hasExistingFiles, setHasExistingFiles] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const fileInputRef = useRef();

  // Initialize PDF viewer plugins
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const thumbnailPluginInstance = thumbnailPlugin();
  const zoomPluginInstance = zoomPlugin();

  const certKey = certification.data?.certificationKey || certification.id;

  // Function to reset form data without affecting the file
  const resetFormData = () => {
    // Reset all fields that can be extracted from PDF
    handleFieldChange('car', '');
    handleFieldChange('address', {});
    handleFieldChange('propertyArea', '');
    handleFieldChange('matriculation', []);
    handleFieldChange('requester', {});
    handleFieldChange('carbonCredit', '');
    handleFieldChange('date', null);
    handleFieldChange('registrationDate', null);
    handleFieldChange('baseYear', {});
    handleFieldChange('owners', []);
    handleFieldChange('userKey', '');
    handleFieldChange('certificationKey', '');
    handleFieldChange('creationCode', '');
  };

  useEffect(() => {
    setIsValidKey(!!certKey && isMD5(certKey));
    
    // Check for existing files when editing a certification
    const checkExistingFiles = async () => {
      if (!certKey) return;
      
      try {
        const pdfRef = ref(storage, `certifications/${certKey}.pdf`);
        const jpgRef = ref(storage, `certifications/${certKey}.jpg`);
        
        // Check if both files exist
        const [pdfExists, jpgExists] = await Promise.all([
          getDownloadURL(pdfRef).then(() => true).catch(() => false),
          getDownloadURL(jpgRef).then(() => true).catch(() => false)
        ]);
        
        setHasExistingFiles(pdfExists && jpgExists);
      } catch (error) {
        console.error('Error checking existing files:', error);
        setHasExistingFiles(false);
      }
    };
    
    checkExistingFiles();
  }, [certKey]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    setIsLoading(true);
    try {
      // Store PDF in memory
      setSelectedPdf(file);

      // Convert PDF to JPG using PDF2Pic
      const jpgBlob = await convertPDFtoJPG(file);
      setSelectedJpg(jpgBlob);

      // Notify parent component about selected files
      onFileSelect({
        pdf: file,
        jpg: jpgBlob
      });

    } catch (error) {
      console.error('Error processing file:', error);
      toast.error('Error processing file');
      setSelectedPdf(null);
      setSelectedJpg(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const pdfRef = ref(storage, `certifications/${certKey}.pdf`);
      const jpgRef = ref(storage, `certifications/${certKey}.jpg`);
      
      await Promise.all([
        deleteObject(pdfRef).catch(console.error),
        deleteObject(jpgRef).catch(console.error)
      ]);
      
      setSelectedPdf(null);
      setSelectedJpg(null);
      if (onFileSelect) {
        onFileSelect(null);
      }
      toast.success('Files deleted successfully!');
    } catch (error) {
      console.error('Error deleting files:', error);
      toast.error('Error deleting files.');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleViewFile = async (fileType) => {
    try {
      // If we have the file in memory (selected but not yet saved)
      if (fileType === 'pdf' && selectedPdf) {
        const url = URL.createObjectURL(selectedPdf);
        setViewingFile(url);
        setViewingFileType('pdf');
        setViewDialogOpen(true);
        return;
      }
      if (fileType === 'jpg' && selectedJpg) {
        const url = URL.createObjectURL(selectedJpg);
        setViewingFile(url);
        setViewingFileType('jpg');
        setViewDialogOpen(true);
        return;
      }

      // If the file is in storage
      if (hasExistingFiles) {
        const fileRef = ref(storage, `certifications/${certKey}.${fileType}`);
        let url;
        
        if (fileType === 'pdf') {
          // Use CORS-aware fetching for PDFs
          url = await getDownloadURL(fileRef);
          url = await fetchPdfWithCors(url);
        } else {
          // Direct URL for images
          url = await getDownloadURL(fileRef);
        }
        
        setViewingFile(url);
        setViewingFileType(fileType);
        setViewDialogOpen(true);
      } else {
        toast.error('No file available to view');
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error(`Error viewing ${fileType.toUpperCase()} file`);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    // Clean up object URLs
    if (viewingFile && (viewingFile.startsWith('blob:') || viewingFile.startsWith('data:'))) {
      URL.revokeObjectURL(viewingFile);
    }
    setViewingFile(null);
    setViewingFileType(null);
  };

  const handleViewPdf = async () => {
    try {
      const pdfRef = ref(storage, `certifications/${certification.data.certificationKey}.pdf`);
      const pdfUrl = await getDownloadURL(pdfRef);
      const corsUrl = await fetchPdfWithCors(pdfUrl);
      // Use corsUrl with your PDF viewer
      setViewingFile(corsUrl);
      setViewingFileType('pdf');
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Error loading PDF file. Please try again.');
    }
  };

  const extractDataFromPDF = async () => {
    if (!selectedPdf) {
      toast.error('Please select a PDF file first');
      return;
    }

    setIsExtracting(true);
    try {
      // Reset form data before extraction
      resetFormData();

      // Load the PDF file
      const arrayBuffer = await selectedPdf.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      // Extract text from all pages
      let extractedText = '';
      let textItems = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1.0 });
        
        // Store items with their positions
        textItems = textItems.concat(
          textContent.items.map(item => ({
            text: item.str,
            x: item.transform[4],
            y: viewport.height - item.transform[5], // Normalize y-coordinate
            width: item.width,
            height: item.height
          }))
        );
      }

      // Group items by lines based on y-position
      const lineThreshold = 5; // pixels
      const lines = [];
      let currentLine = [];
      
      // Sort initially by y position
      textItems.sort((a, b) => a.y - b.y);
      
      textItems.forEach((item) => {
        if (currentLine.length === 0) {
          currentLine.push(item);
        } else {
          const lastItem = currentLine[currentLine.length - 1];
          if (Math.abs(item.y - lastItem.y) <= lineThreshold) {
            currentLine.push(item);
          } else {
            // Sort items in the current line by x position
            currentLine.sort((a, b) => a.x - b.x);
            lines.push([...currentLine]);
            currentLine = [item];
          }
        }
      });
      
      // Don't forget the last line
      if (currentLine.length > 0) {
        currentLine.sort((a, b) => a.x - b.x);
        lines.push(currentLine);
      }

      // Join items into text, with proper line breaks
      extractedText = lines
        .map(line => line.map(item => item.text).join(' '))
        .join('\n');

      // Define regex patterns for field extraction
      const patterns = {
        car: /CAR\s*:\s*([A-Z]{2}-\d+-(?:[A-F0-9]{4}\.?){7}[A-F0-9]{4})/i,
        propertyName: /Property Name\s*:\s*([^\n]+)/i,
        propertyArea: /Property Area\s*:\s*([\d,.]+)\s*(?:ha|acres)/i,
        city: /City\s*:\s*([^\n]+)/i,
        state: /State\s*:\s*([^\n]+)/i,
        country: /Country\s*:\s*([^\n]+)/i,
        latitude: /Latitude\s*:\s*([-\d.°'",\s]+S)/i,
        longitude: /Longitude\s*:\s*([-\d.°'",\s]+[OW])/i,
        requesterName: /Requester\s*:\s*([^\n]+?)(?=\s*(?:Longitude|Latitude|E-mail|Phone|Country|State|City|$))/i,
        requesterEmail: /E-mail\s*:\s*([^\n]+?)(?=\s*(?:Longitude|Matriculation|Phone|$))/i,
        phone: /Phone\s*:\s*([^\n]+)/i,
        matriculation: /Matriculation\s*:\s*([\d,.\s|]+)/i,
        carbonCredit: /CARBON CREDIT\s*:\s*([\d,.]+\s*CCO2)/i,
        requestDate: /Data Date\s*:([^a-zA-Z]*\d+[^a-zA-Z]*\d+[^a-zA-Z]*\d+[^a-zA-Z]*\d+[^a-zA-Z]*\d+[^a-zA-Z]*\d+[^a-zA-Z]*)/i,
        registrationDate: /Registration Date\s*:\s*(\d{2}\/\d{2}\/\d{4})/i,
        baseYear: /Base Year\s*:\s*(\d{2}\/\d{2}\/\d{4})\s*(?:a|to|-)\s*(\d{2}\/\d{2}\/\d{4})/i,
        owners: /Full Name\s*:\s*([^\n]+?)\s*ID\s*:\s*\.?\s*([\d,./-]+)/i,
        userKey: /User\s*:\s*([a-f0-9]{40})/i,
        certificationKey: /Certification Key\s*:\s*([a-f0-9]{32})/i,
        creationCode: /^\s*Creation Transaction Code:\s*([A-Fa-f0-9]{32})/m,
      };

      // Extract and update fields
      Object.entries(patterns).forEach(([field, pattern]) => {
        // Special handling for owners field
        if (field === 'owners') {
          const ownerMatches = Array.from(extractedText.matchAll(new RegExp(pattern, 'gi')));
          
          if (ownerMatches.length > 0) {
            const owners = ownerMatches
              .map(match => {
                const fullName = match[1].trim();
                const rawId = match[2].trim();
                     
                if (!fullName || !rawId) {
                  return null;
                }
                
                const docId = rawId.replace(/[^\d]/g, '');
                
                let docType;
                if (docId.length === 11) {
                  docType = 'CPF';
                } else if (docId.length === 14) {
                  docType = 'CNPJ';
                } else {
                  return null;
                }
                
                return {
                  fullName,
                  docType,
                  docId
                };
              })
              .filter(owner => owner !== null);
            
            if (owners.length > 0) {
              handleFieldChange('owners', owners);
            }
          }
          return; // Skip the rest of the processing for owners
        }

        // Regular handling for other fields
        const match = extractedText.match(pattern);
        if (match) {
          const value = match[1].trim();
          switch (field) {
            case 'car':
              if (value) {
                const formattedCAR = formatCAR(value.trim());
                handleFieldChange('car', formattedCAR);
              }
              break;
            case 'propertyName':
              const cleanPropertyName = value.replace(/\s+(?:Registration|Matriculation|Property Area|CAR|City|State|Country|Phone|E-mail|CCO2|Base Year|Full Name).*$/, '').trim();
              handleFieldChange('address.propertyName', cleanPropertyName);
              break;
            case 'propertyArea':
              const areaValue = value.replace(',', '.');
              const areaNumber = parseFloat(areaValue);
              handleFieldChange('propertyArea', areaNumber);
              break;
            case 'city':
              if (!certification.data.address) {
                handleFieldChange('address', {});
              }
              const cleanCity = value.replace(/\s+(?:State|Country|Phone|CAR|Registration|Address|E-mail|CCO2|Full Name).*$/, '').trim();
              handleFieldChange('address.city', cleanCity);
              break;
            case 'state':
              if (!certification.data.address) {
                handleFieldChange('address', {});
              }
              const cleanState = value.replace(/\s+(?:Phone|CAR|Registration|Address|E-mail|CCO2|Full Name).*$/, '').trim();
              handleFieldChange('address.state', cleanState);
              break;
            case 'country':
              if (!certification.data.address) {
                handleFieldChange('address', {});
              }
              const cleanCountry = value.replace(/\s+(?:City|State|Phone|CAR|Registration|Address|E-mail|CCO2|Full Name).*$/, '').trim();
              handleFieldChange('address.country', cleanCountry);
              break;
            case 'latitude':
              if (!certification.data.address) {
                handleFieldChange('address', {});
              }
              handleFieldChange('address.latitude', value);
              break;
            case 'longitude':
              if (!certification.data.address) {
                handleFieldChange('address', {});
              }
              handleFieldChange('address.longitude', value);
              break;
            case 'requesterName':
              if (!certification.data.requester) {
                handleFieldChange('requester', {});
              }
              const cleanRequesterName = value.replace(/\s+(?:Data Date|Latitude|Longitude|Matriculation|Property Area|E-mail|Phone|Country|State|City|CCO2|Property Name|Registration Date|Base Year).*$/, '').trim();
              handleFieldChange('requester.fullName', cleanRequesterName);
              break;
            case 'requesterEmail':
              if (!certification.data.requester) {
                handleFieldChange('requester', {});
              }
              const cleanEmail = value.replace(/\s+(?:CCO2|Property Name|Registration Date).*$/, '').trim();
              handleFieldChange('requester.email', cleanEmail);
              break;
            case 'phone':
              if (!certification.data.requester) {
                handleFieldChange('requester', {});
              }
              // Extract country code and local number from full phone number
              const country = COUNTRY_CODES.find(c => value.startsWith(c.dialCode));
              if (country) {
                // Remove country code and trim spaces to get local number
                const cleanPhone = value.replace(country.dialCode, '').trim();
                const formattedPhone = formatPhoneNumber(cleanPhone, country.code);
                const fullNumber = `${COUNTRY_CODES.find(c => c.code === country.code).dialCode} ${formattedPhone}`;
                handleFieldChange('requester.phone', fullNumber);
              } else {
                toast.error('Invalid phone number. No country code found.');
              }
              
              break;
            case 'matriculation':
              if (!certification.data.matriculation || !Array.isArray(certification.data.matriculation)) {
                handleFieldChange('matriculation', []);
              }
              
              const matriculationNumbers = value
                .split('|')
                .map(num => num.trim().replace(/[.,]/g, ''))
                .filter(num => num.length > 0 && /^\d+$/.test(num))
                .map(num => parseInt(num));
            
              handleFieldChange('matriculation', matriculationNumbers);
              break;
            case 'carbonCredit':
              const creditMatch = value.match(/([\d.]+)/);
              if (creditMatch) {
                // Remove dots (thousand separators), then parse as integer
                const creditValue = creditMatch[1].replace(/\./g, '');
                handleFieldChange('carbonCredit', parseInt(creditValue));
              }
              break;
            case 'requestDate':
              if (match && match[1]) {
                const digits = match[1].match(/\d/g);
                if (digits && digits.length >= 6) {
                  const month = `${digits[0]}${digits[1]}`;
                  const day = `${digits[2]}${digits[3]}`;
                  const year = `${digits[4]}${digits[5]}`;
                  const dateStr = `${month}/${day}/${year}`;
                  const timestamp = createTimestamp(dateStr, 'MM/DD/YYYY');
                  if (timestamp) {
                    handleFieldChange('date', timestamp);
                  }
                }
              }
              break;
            case 'registrationDate':
              const timestamp = createTimestamp(match[1], 'MM/DD/YYYY');
              if (timestamp) {
                handleFieldChange('registrationDate', timestamp);
              }
              break;
            case 'baseYear':
              if (!certification.data.baseYear) {
                handleFieldChange('baseYear', {});
              }
              const startTimestamp = createTimestamp(match[1], 'MM/DD/YYYY');
              const endTimestamp = createTimestamp(match[2], 'MM/DD/YYYY', true);
              if (startTimestamp && endTimestamp) {
                handleFieldChange('baseYear.startDate', startTimestamp);
                handleFieldChange('baseYear.endDate', endTimestamp);
              }
              break;
            case 'userKey':
              handleFieldChange('userKey', match[1]);
              break;
            case 'certificationKey':
              handleFieldChange('certificationKey', match[1]);
              break;
            case 'creationCode':
              handleFieldChange('creationCode', match[1]);
              break;
          }
        }
      });

      // Initialize transactions array if it doesn't exist
      if (!certification.data.transactions || !Array.isArray(certification.data.transactions)) {
        handleFieldChange('transactions', []);
      }

      toast.success('Data extracted successfully');
    } catch (error) {
      console.error('Error extracting data:', error);
      toast.error('Error extracting data from PDF');
    } finally {
      setIsExtracting(false);
    }
  };

  const renderFileStatus = () => {
    if (isLoading) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <RefreshIcon sx={{ fontSize: 30, color: '#ffd700' }} />
          <Typography variant="body1">
            Processing file...
          </Typography>
        </Box>
      );
    }

    if (selectedPdf) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <PendingIcon sx={{ fontSize: 30, color: 'rgb(255 189 0)' }} />
          <Typography variant="body1">
            New file selected: {selectedPdf.name}
            {hasExistingFiles && ' (will replace existing file)'}
          </Typography>
          <Tooltip title="View PDF">
            <IconButton onClick={() => handleViewFile('pdf')} size="large">
              <PdfIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Image">
            <IconButton onClick={() => handleViewFile('jpg')} size="large">
              <ImageIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Extract Data from PDF">
            <span>
              <IconButton 
                onClick={extractDataFromPDF}
                disabled={isExtracting || hasExistingFiles}
                size="large"
              >
                <ExtractIcon sx={{ fontSize: 30 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    }

    if (hasExistingFiles) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <CheckIcon sx={{ fontSize: 30, color: '#4caf50' }} />
          <Typography variant="body1">
            Files uploaded and available
          </Typography>
          <Tooltip title="View PDF">
            <IconButton onClick={() => handleViewFile('pdf')} size="large">
              <PdfIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Image">
            <IconButton onClick={() => handleViewFile('jpg')} size="large">
              <ImageIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Extract Data from PDF">
            <span>
              <IconButton 
                onClick={extractDataFromPDF}
                disabled={isExtracting || hasExistingFiles}
                size="large"
              >
                <ExtractIcon sx={{ fontSize: 30 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CancelIcon sx={{ fontSize: 30, color: '#f44336' }} />
        <Typography variant="body1">
          No files uploaded
        </Typography>
      </Box>
    );
  };

  return (
    <Grid item xs={12} style={{ border: '1px solid #ccc', margin: '30px 0 0 25px', padding: '20px', borderRadius: '8px' }}>
      <Typography variant="h6" gutterBottom>
        Certification Files
      </Typography>
      
      <Grid container spacing={3} direction="column">
        <Grid item>
          <Box display="flex" alignItems="center" gap={2}>
            <input
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              ref={fileInputRef}
            />
            
            <Button
              variant="contained"
              onClick={() => fileInputRef.current.click()}
              disabled={isLoading}
              startIcon={selectedPdf ? <ChangeIcon /> : <PdfIcon />}
            >
              {hasExistingFiles ? 'Replace File' : 'Select File'}
            </Button>

            {renderFileStatus()}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          View {viewingFileType?.toUpperCase()}
          <IconButton
            aria-label="close"
            onClick={handleCloseViewDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewingFile && (
            viewingFileType === 'pdf' ? (
              <div style={{ height: '80vh' }}>
                <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.0.279/pdf.worker.min.js">
                  <Viewer
                    fileUrl={viewingFile}
                    plugins={[
                      defaultLayoutPluginInstance,
                      thumbnailPluginInstance,
                      zoomPluginInstance,
                    ]}
                  />
                </Worker>
              </div>
            ) : (
              <img
                src={viewingFile}
                alt="Certification"
                style={{ width: '100%', height: 'auto' }}
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Delete Files
          <IconButton
            aria-label="close"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete these files?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default FileUploadSection; 
